import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Search, Calendar } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { adminPromotionsAPI } from '../../utils/api';
import { toast } from '../../hooks/use-toast';
import { Toaster } from '../ui/toaster';

const PromotionsManagement = () => {
  const [promotions, setPromotions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedPromotion, setSelectedPromotion] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    legal_terms: '',
    start_date: '',
    end_date: '',
    status: 'draft',
  });
  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 15,
    total: 0,
  });

  useEffect(() => {
    loadPromotions();
  }, [searchTerm]);

  const loadPromotions = async (page = 1) => {
    try {
      setLoading(true);
      const params = {
        page,
        per_page: 15,
        ...(searchTerm && { search: searchTerm }),
      };
      const response = await adminPromotionsAPI.getAll(params);
      setPromotions(response.data);
      if (response.pagination) {
        setPagination(response.pagination);
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || "No se pudieron cargar las promociones",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (promotion = null) => {
    if (promotion) {
      setSelectedPromotion(promotion);
      setFormData({
        name: promotion.name || '',
        legal_terms: promotion.legal_terms || '',
        start_date: promotion.start_date || '',
        end_date: promotion.end_date || '',
        status: promotion.status || 'draft',
      });
    } else {
      setSelectedPromotion(null);
      setFormData({
        name: '',
        legal_terms: '',
        start_date: '',
        end_date: '',
        status: 'draft',
      });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedPromotion(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validar fechas
    if (new Date(formData.start_date) > new Date(formData.end_date)) {
      toast({
        title: "Error",
        description: "La fecha de inicio debe ser anterior a la fecha de fin",
        variant: "destructive",
      });
      return;
    }

    try {
      if (selectedPromotion) {
        await adminPromotionsAPI.update(selectedPromotion.id, formData);
        toast({
          title: "Éxito",
          description: "Promoción actualizada exitosamente",
        });
      } else {
        await adminPromotionsAPI.create(formData);
        toast({
          title: "Éxito",
          description: "Promoción creada exitosamente",
        });
      }
      handleCloseModal();
      loadPromotions();
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || "Error al guardar la promoción",
        variant: "destructive",
      });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Estás seguro de eliminar esta promoción?')) {
      return;
    }
    try {
      await adminPromotionsAPI.delete(id);
      toast({
        title: "Éxito",
        description: "Promoción eliminada exitosamente",
      });
      loadPromotions();
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || "Error al eliminar la promoción",
        variant: "destructive",
      });
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-green-500/20 text-green-400';
      case 'inactive':
        return 'bg-red-500/20 text-red-400';
      default:
        return 'bg-gray-500/20 text-gray-400';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'active':
        return 'Activa';
      case 'inactive':
        return 'Inactiva';
      default:
        return 'Borrador';
    }
  };

  return (
    <div className="p-6 space-y-6">
      <Toaster />
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Promociones</h2>
          <p className="text-[#A2A2A2]">Gestiona las promociones del outlet</p>
        </div>
        <Button
          onClick={() => handleOpenModal()}
          className="bg-white text-black hover:bg-white/90"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nueva promoción
        </Button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#A2A2A2] w-5 h-5" />
        <Input
          type="text"
          placeholder="Buscar por nombre..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10 bg-white/5 border-white/10 text-white"
        />
      </div>

      {/* Table */}
      {loading ? (
        <div className="text-center py-12 text-[#A2A2A2]">Cargando...</div>
      ) : promotions.length === 0 ? (
        <div className="text-center py-12 text-[#A2A2A2]">No hay promociones registradas</div>
      ) : (
        <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[700px]">
            <thead className="bg-white/5">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-medium text-white">Nombre</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-white">Vigencia</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-white">Estatus</th>
                <th className="px-6 py-4 text-right text-sm font-medium text-white">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/10">
              {promotions.map((promotion) => (
                <tr key={promotion.id} className="hover:bg-white/5">
                  <td className="px-6 py-4 text-white font-medium">{promotion.name}</td>
                  <td className="px-6 py-4 text-[#A2A2A2] text-sm">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      {promotion.start_date} - {promotion.end_date}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(promotion.status)}`}>
                      {getStatusLabel(promotion.status)}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleOpenModal(promotion)}
                        className="text-white hover:bg-white/10"
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(promotion.id)}
                        className="text-red-400 hover:bg-red-500/10"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {/* Pagination */}
      {pagination.last_page > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-[#A2A2A2] text-sm">
            Mostrando {((pagination.current_page - 1) * pagination.per_page) + 1} a {Math.min(pagination.current_page * pagination.per_page, pagination.total)} de {pagination.total}
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => loadPromotions(pagination.current_page - 1)}
              disabled={pagination.current_page === 1}
            >
              Anterior
            </Button>
            <Button
              variant="outline"
              onClick={() => loadPromotions(pagination.current_page + 1)}
              disabled={pagination.current_page === pagination.last_page}
            >
              Siguiente
            </Button>
          </div>
        </div>
      )}

      {/* Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="bg-black border-white/10 text-white max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl">
              {selectedPromotion ? 'Editar promoción' : 'Nueva promoción'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="name">Nombre promoción *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                className="bg-white/5 border-white/10 text-white"
              />
            </div>
            <div>
              <Label htmlFor="legal_terms">Condiciones legales *</Label>
              <textarea
                id="legal_terms"
                value={formData.legal_terms}
                onChange={(e) => setFormData({ ...formData, legal_terms: e.target.value })}
                rows={6}
                required
                className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-[#A2A2A2] focus:outline-none focus:ring-2 focus:ring-white/20"
                placeholder="Ingresa las condiciones legales de la promoción..."
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="start_date">Fecha inicio *</Label>
                <Input
                  id="start_date"
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  required
                  className="bg-white/5 border-white/10 text-white"
                />
              </div>
              <div>
                <Label htmlFor="end_date">Fecha fin *</Label>
                <Input
                  id="end_date"
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                  required
                  className="bg-white/5 border-white/10 text-white"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="status">Estatus *</Label>
              <select
                id="status"
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                required
                className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-white/20"
              >
                <option value="draft">Borrador</option>
                <option value="active">Activa</option>
                <option value="inactive">Inactiva</option>
              </select>
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={handleCloseModal}
              >
                Cancelar
              </Button>
              <Button type="submit" className="bg-white text-black hover:bg-white/90">
                {selectedPromotion ? 'Actualizar' : 'Crear'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PromotionsManagement;

